import React from 'react';
import PropTypes from 'prop-types';
import CircularProgress from '../CircularProgress';
import TrophyTotals from '../TrophyTotals';
import { classNamesFor } from '../utils/classNames';
var cx;
/**
 * The TrophyOverview component shows a high level view of the the users
 * trophy activity
 *
 * ## Composed of
 * - [CircularProgress](../CircularProgress/README.md)
 * - [TrophyTotals](../TrophyTotals/README.md)
 *
 * ## Related Components
 * - [Trophy](../Trophy/README.md)
 *
 * ## Usage
 *
 *```jsx
 * <TrophyOverview
 *   availableCount={120}
 *   earnedCount={40}
 *   platinumCount={2}
 *   goldCount={8}
 *   silverCount={19}
 *   bronzeCount={11}
 * />
 * ```
 *
 * ## Props
 */

var TrophyOverview = function TrophyOverview(props) {
  var qa = props.qa,
      className = props.className,
      availableCount = props.availableCount,
      earnedCount = props.earnedCount,
      platinumCount = props.platinumCount,
      goldCount = props.goldCount,
      silverCount = props.silverCount,
      bronzeCount = props.bronzeCount;

  var _TrophyOverview$getCl = TrophyOverview.getClassName({
    className: className
  }),
      root = _TrophyOverview$getCl.root,
      primaryMeta = _TrophyOverview$getCl.primaryMeta,
      secondaryText = _TrophyOverview$getCl.secondaryText,
      primarySection = _TrophyOverview$getCl.primarySection,
      trophyTotals = _TrophyOverview$getCl.trophyTotals;

  return /*#__PURE__*/React.createElement("span", {
    "data-qa": qa,
    className: root
  }, /*#__PURE__*/React.createElement("div", {
    className: primarySection
  }, /*#__PURE__*/React.createElement("span", {
    className: primaryMeta
  }, /*#__PURE__*/React.createElement("h3", {
    "data-qa": "".concat(qa, "#earnedCount"),
    className: "psw-s-num"
  }, earnedCount), /*#__PURE__*/React.createElement("p", {
    className: secondaryText
  }, "Earned")), /*#__PURE__*/React.createElement(CircularProgress, {
    qa: "".concat(qa, "#progress"),
    max: availableCount,
    value: earnedCount
  }), /*#__PURE__*/React.createElement("span", {
    className: primaryMeta
  }, /*#__PURE__*/React.createElement("h3", {
    "data-qa": "".concat(qa, "#availableCount"),
    className: "psw-s-num"
  }, availableCount), /*#__PURE__*/React.createElement("p", {
    className: secondaryText
  }, "Available"))), /*#__PURE__*/React.createElement(TrophyTotals, {
    qa: "".concat(qa, "#trophyTotals"),
    className: trophyTotals,
    layout: "v",
    size: "l",
    platinumCount: platinumCount,
    goldCount: goldCount,
    silverCount: silverCount,
    bronzeCount: bronzeCount
  }));
};

TrophyOverview.useClassNames = function (classNames) {
  TrophyOverview.classNames = classNames;
};

TrophyOverview.getClassName = function (_ref) {
  var className = _ref.className;
  return {
    root: cx('psw-trophy-overview', 'psw-l-stack-center', 'psw-bg-2', 'psw-p-s', className),
    primaryMeta: cx('psw-p-s', 'psw-l-stack-center'),
    primarySection: cx('psw-p-b-s', 'psw-l-line-center'),
    secondaryText: cx('psw-c-secondary', 'psw-body-2'),
    trophyTotals: cx('psw-fill-x', 'psw-l-line-repel')
  };
};

TrophyOverview.defaultProps = {
  qa: '',
  className: '',
  availableCount: 1,
  earnedCount: 0,
  platinumCount: 0,
  goldCount: 0,
  silverCount: 0,
  bronzeCount: 0
};
TrophyOverview.propTypes = {
  /**
   * QA selector attribute used to target important elements for test validation
   */
  qa: PropTypes.string,

  /**
   * Allows adding a custom class or series of classes on the component
   * useful for setting margin or padding
   */
  className: PropTypes.string,

  /**
   * The total number of earnable trophies
   */
  availableCount: PropTypes.number,

  /**
   * The total number of trophies the user has earned
   */
  earnedCount: PropTypes.number,

  /**
   * The total number of platinum trophies earned
   */
  platinumCount: PropTypes.number,

  /**
   * The total number of gold trophies earned
   */
  goldCount: PropTypes.number,

  /**
   * The total number of silver trophies earned
   */
  silverCount: PropTypes.number,

  /**
   * The total number of bronze trophies earned
   */
  bronzeCount: PropTypes.number
};
cx = classNamesFor(TrophyOverview);
export default TrophyOverview;